/** Printable map wizard — options and leftover fulfilment helpers.

The wizard is a PDF download: Boundary → Map Options → Preview → Review & Print.
`computePricing` / `formatMoney` remain for tests and any legacy order UI; the
wizard no longer uses them.
*/

export const WIZARD_STEPS = ['Boundary', 'Map Options', 'Preview', 'Review & Print'];

export function canContinueStep(step, state = {}) {
  if (step === 1) return Boolean(state.huntBoundaryId);
  if (step === 2) return Boolean(state.title?.trim());
  if (step === 3) return Boolean(state.previewReady);
  return false;
}

/**
 * `available` tracks what the print renderer can actually compose. Only aerial
 * imagery is wired to a printable source today; the others need their own source
 * composition, so offering them would produce an order we cannot fulfil.
 */
export const STYLES = [
  {
    value: 'satellite',
    label: 'Satellite',
    code: 'SAT',
    swatch: 'linear-gradient(135deg, #2c3626, #46402c 55%, #5d4a2f)',
    available: true,
  },
  {
    value: 'topographic',
    label: 'Topographic',
    code: 'TOPO',
    swatch:
      'repeating-linear-gradient(115deg, transparent 0 6px, color-mix(in srgb, var(--color-brand-accent) 40%, transparent) 6px 7px), color-mix(in srgb, var(--color-brand-primary) 14%, var(--color-surface-muted))',
    available: false,
  },
  {
    value: 'clean',
    label: 'Clean',
    code: 'CLN',
    swatch:
      'linear-gradient(180deg, color-mix(in srgb, var(--color-brand-primary) 8%, var(--color-surface)), var(--color-surface))',
    available: false,
  },
  {
    value: 'vintage',
    label: 'Vintage',
    code: 'VTG',
    swatch: 'linear-gradient(135deg, #cdb892, #a98c5d)',
    available: false,
  },
];

export const DEFAULT_SIZE = '18x24';

/** Retail print-shop sizes. Prices belong to the shop, not to Yona. */
export const SIZES = [
  { value: '12x18', label: '12 × 18″' },
  { value: '18x24', label: '18 × 24″' },
  { value: '24x36', label: '24 × 36″' },
  { value: '36x48', label: '36 × 48″' },
];

/** Backend material values — labels/descriptions are design-facing only. */
export const MATERIALS = [
  {
    value: 'matte',
    label: 'Matte paper',
    desc: 'Archival 230gsm, low-glare finish.',
    add: 0,
  },
  {
    value: 'canvas',
    label: 'Canvas',
    desc: 'Gallery-wrapped canvas finish.',
    add: 15,
  },
  {
    value: 'framed',
    label: 'Framed',
    desc: 'Matted and framed for clubhouse display.',
    add: 25,
  },
];

export const LAYER_DEFS = [
  { key: 'stands', label: 'Stand markers', desc: 'Numbered stand and blind locations.', stubbed: true },
  { key: 'plots', label: 'Food plots', desc: 'Planted plots and feeders.', stubbed: true },
];

export const DEFAULT_LAYERS = {
  stands: true,
  plots: true,
};

export const OUTLINE_SWATCHES = [
  { value: '#c2772e', label: 'Cognac' },
  { value: '#3f6b43', label: 'Forest' },
  { value: '#a23f2a', label: 'Rust' },
  { value: '#3c5a6e', label: 'Slate' },
  { value: '#2b2b28', label: 'Charcoal' },
  { value: '#ffffff', label: 'White' },
];

export const DEFAULT_OUTLINE_COLOR = '#ffffff';
export const DEFAULT_LOGO_POSITION = 'left';
export const LOGO_POSITIONS = [
  { value: 'left', label: 'Bottom left' },
  { value: 'right', label: 'Bottom right' },
];
export const BOUNDARY_LINE_SCALE_MIN = 0.5;
export const BOUNDARY_LINE_SCALE_MAX = 2;
export const DEFAULT_BOUNDARY_LINE_SCALE = BOUNDARY_LINE_SCALE_MAX;
export const ORIENTATIONS = [
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
];
export const DEFAULT_ORIENTATION = 'landscape';
export const SHIPPING_RATE = 14;
export const PREVIEW_FRAME_HEIGHT = 380;

export function formatMoney(amount) {
  return `$${Number(amount).toLocaleString()}`;
}

export function getSizeOption(sizeValue) {
  return SIZES.find((s) => s.value === sizeValue)
    || SIZES.find((s) => s.value === DEFAULT_SIZE);
}

export function getMaterialOption(materialValue) {
  return MATERIALS.find((m) => m.value === materialValue) || MATERIALS[0];
}

export function getStyleOption(styleValue) {
  return STYLES.find((s) => s.value === styleValue) || STYLES[0];
}

export function computePricing(sizeValue, materialValue, shippingRate = SHIPPING_RATE) {
  const size = getSizeOption(sizeValue);
  const material = getMaterialOption(materialValue);
  const sizePrice = Number(size.price) || 0;
  const subtotal = sizePrice + material.add;
  const total = subtotal + shippingRate;
  return { size, material, subtotal, shipping: shippingRate, total };
}

export function formatCoords(lat, lng) {
  if (lat == null || lng == null) return null;
  const latStr = Number(lat).toFixed(4);
  const lngStr = Number(lng).toFixed(4);
  return `LAT ${latStr} | LONG ${lngStr}`;
}
